export const DEFAULT_LEAD_BUSINESS_DAYS = 5;

export const fmtCcy = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
export const fmtUnits = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 10,
  });
export const fmtPrice = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  });
export const fmtNum = (value: number): string => value.toLocaleString("en-US");

// Diffs are decimal subtractions that leave floating-point residue: a unit
// difference of 4e-12 is zero for every purpose except a naive `> 0` sign test,
// which renders it as "+0.0000". Anything inside these bounds is shown unsigned.
const AMOUNT_EPSILON = 0.005;
const UNIT_EPSILON = 1e-9;

// The one zero token this table uses. Units format to 4 decimals, so falling
// back to fmtUnits(0) here would print 0.0000 in a column whose every other
// zero reads 0.00.
export const ZERO_DIFF = "0.00";

export const signedCcy = (value: number): string =>
  Math.abs(value) < AMOUNT_EPSILON
    ? ZERO_DIFF
    : `${value > 0 ? "+" : "\u2212"}${fmtCcy(Math.abs(value))}`;

export const signedUnits = (value: number): string =>
  Math.abs(value) < UNIT_EPSILON
    ? ZERO_DIFF
    : `${value > 0 ? "+" : "\u2212"}${fmtUnits(Math.abs(value))}`;

export const isEffectivelyZero = (value: number | null | undefined): boolean =>
  value == null || Math.abs(value) < AMOUNT_EPSILON;

export function daysUntil(iso: string | undefined | null): number {
  if (!iso) return 0;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return 0;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

export function formatFriendly(
  iso: string | undefined | null,
  withTime = false,
): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const datePart = date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  if (!withTime) return datePart;
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} at ${timePart}`;
}

export function formatFriendlyTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function isoForBusinessDayOffset(
  offset: number,
  hour: number,
  minute: number,
): string {
  const date = new Date();
  let remaining = offset;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) remaining--;
  }
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
}

export function isoForEveningRun(): string {
  const date = new Date();
  date.setHours(19, 0, 0, 0); // recon runs in the 7–11:30 PM window
  return date.toISOString();
}

export function countdownLabel(iso: string | undefined | null): {
  text: string;
  cls: string;
} {
  const days = daysUntil(iso);
  if (days <= 0) return { text: "Runs today", cls: "now" };
  if (days === 1) return { text: "in 1 day", cls: "soon" };
  if (days <= 3) return { text: `in ${days} days`, cls: "soon" };
  return { text: `in ${days} days`, cls: "" };
}

export function productColor(index: number): string {
  return `hsl(${(index * 73 + 200) % 360} 65% 55%)`;
}

export function numOr(input: string, fallback: number): number {
  const parsed = parseFloat(input);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export const RUN_WINDOW_OPENS_HOUR = 19;
export const RUN_WINDOW_CLOSES_HOUR = 6;

export function runWindowOpen(at: Date = new Date()): boolean {
  const hour = at.getHours();
  return hour >= RUN_WINDOW_OPENS_HOUR || hour < RUN_WINDOW_CLOSES_HOUR;
}

export const STUCK_AFTER_MS = 15 * 60 * 1000;

export function isStuck(record: {
  status: string;
  uploadedAt: string;
}): boolean {
  return (
    record.status === "parsing" &&
    Date.now() - new Date(record.uploadedAt).getTime() > STUCK_AFTER_MS
  );
}
